import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'

import Input from '@/components/forms/Input/Input'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import routes from '@/routes/routes'
import useAuthStore, { type ResetPasswordRequest } from '@/stores/AuthStore'

import '../Auth/Auth.css'
import '@/styles/General.css'

const ForgotPassword = () => {
    const { t } = useTranslation('common')
    const navigate = useNavigate()
    const { sendResetPasswordRequest, isLoading, isLogged } = useAuthStore()

    const [formData, setFormData] = useState<ResetPasswordRequest>({
        email: '',
    })

    useEffect(() => {
        if (isLogged) {
            navigate(routes.home)
        }
    }, [isLogged, navigate])

    const handleFormDataChange = (key: string, value: string) => {
        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const validateForm = (formData: ResetPasswordRequest) => {
        const { email } = formData;
        
        if (!email) {
            return { isValid: false, message: t('missing_fields') }
        }
        
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return { isValid: false, message: t('invalid_email') }
        }
        
        return { isValid: true, message: '' }
    }

    const onSubmit = async (e: any) => {
        e.preventDefault();

        const validation = validateForm(formData)
        if (!validation.isValid) {
            toast.error(validation.message);
            return;
        }

        try {
            const status: boolean = await sendResetPasswordRequest(formData);
            if (status) {
                navigate(routes.auth);
                return;
            }
        } catch {
            toast.error(t('error'))
        }
    }

    return (
        <div className="auth-page">
            <div className="auth-container">
                {/* Welcome Section */}
                <div className="auth-welcome">
                    <img className="auth-logo-persistent" src="/domber.png" alt="Logo Domber" />
                    
                    <div className="auth-welcome-content">
                        <h1 className="auth-welcome-title auth-form-title">{t('login_forgot_password_title') || '¿Olvidaste tu contraseña?'}</h1>
                        <p className="auth-welcome-tagline">Te enviaremos un enlace de recuperación</p>
                    </div>
                </div>

                {/* Footer */}
                <div className="auth-footer-global">
                    © 2025 - Domber
                </div>

                {/* Form */}
                <div className="auth-form-container">
                    <div className="auth-card-body">
                        <form className="auth-form" onSubmit={onSubmit}>
                            <Input
                                name="email"
                                type="email"
                                value={formData.email}
                                setValue={(value) => handleFormDataChange('email', value)}
                                label={t('email_label')}
                                placeholder={t('email_placeholder')}
                            />
                            <Button variant="primary" size="lg" type="submit" disabled={isLoading}>
                                {isLoading ? <Spinner variant="secondary" size="xs" /> : (t('login_reset_password') || 'Enviar enlace')}
                            </Button>
                        </form>
                        <div className="auth-separator"></div>
                        <div className="auth-links">
                            <Button
                                variant="secondary"
                                size="md"
                                onClick={() => navigate(routes.auth)}
                                className="w-full"
                                disabled={isLoading}
                            >
                                Volver al inicio de sesión
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ForgotPassword
